import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { InvoiceService } from "../../../invoice.service";
import { IContentHeader } from "../../../../../layout/components/content-header/content-header.component";
import moment from "moment";
import { ICustomer } from "../../../../customer/customer.interface";
import { CustomerService } from "../../../../customer/customer.service";
import { IInvoice } from "../../../interfaces/invoice.interface";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { ListService } from "../../../../../auth/service/list.service";
import { AuthenticationService, UserService } from "../../../../../auth/service";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductsService } from "../../../../products/products.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
  selector: "app-invoice-to-create",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./invoice-to-create.component.html"
})
export class InvoiceToCreateComponent implements OnInit, OnDestroy {
  contentHeader: IContentHeader;
  invoices: IInvoice[] = [];
  filteredInvoices: IInvoice[] = [];
  tableLimit: number = 10;
  filterSearch: string = "";
  filterCustomer: number = 0;
  filterYear: number = moment().year();
  filterMonth: number = 0;
  availableMonths = Array.from({ length: 12 }, (_, i) => i + 1);
  availableYears = Array.from({ length: moment().year() - 2023 + 1 }, (_, i) => 2023 + i);
  customersList: ICustomer[] = [];
  idsToGenerate: number[] = [];
  showModalGenerate: boolean = false;
  rows_types: any = [];
  payment_measure_units: any = [];
  invoice_document_types: any = [];
  credit_note_types: any = [];
  payment_terms: any = [];
  invoice_services: any = [];
  filter_type_order: number = -1;
  filter_detachment: number = 0;
  checkProductsList = false;

  sumTotalsAmount: number = 0;
  private _unsubscribeAll: Subject<any>;

  constructor(
    private _invoiceService: InvoiceService,
    private _customerService: CustomerService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _utilsService: UtilsService,
    private _listService: ListService,
    public _userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    public _productService: ProductsService,
    private _authenticationService: AuthenticationService
  ) {
    this._utilsService.disableAutoHideLoading();
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.contentHeader = {
      headerTitle: "MENU.INVOICES_TO_GENERATE",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/"
          },
          {
            name: "MENU.INVOICES",
            isLink: false
          },
          {
            name: "MENU.INVOICES_TO_GENERATE",
            isLink: false
          }
        ]
      }
    };
  }

  ngOnInit() {
    this._utilsService.showLoading();
    this._invoiceService.getToGenerate().pipe(takeUntil(this._unsubscribeAll)).subscribe(() => this.loadData());

    this._customerService.getCustomers().then((result: any) => (this.customersList = result));
    this._userService.getAllUsers();
    this._listService.getList("invoice_row_type").then((result: any) => {
      this.rows_types = result;
    });
    this._listService.getList("payment_measure_unit").then((result: any) => {
      this.payment_measure_units = result;
    });
    this._listService.getList("invoice_document_type").then((result: any) => {
      this.invoice_document_types = result.filter((item: any) => !item.other.credit_note);
      this.credit_note_types = result.filter((item: any) => item.other.credit_note);
    });
    this._listService.getList("payment_terms").then((result: any) => {
      this.payment_terms = result;
    });
    this._listService.getList("invoice_service").then((result: any) => {
      this.invoice_services = result;
    });

    this.route.queryParams.subscribe(params => {
      this.filterSearch = params.filterSearch || "";
      this.filterCustomer = Number(params.filterCustomer || "0");
      this.filterYear = Number(params.filterYear || this.filterYear);
      this.filterMonth = Number(params.filterMonth || this.filterMonth);
    });

    if (!this.checkProductsList && this._productService.productsList.length < 1) {
      this.checkProductsList = true;
      this._productService.getProductsList().then((results: any) => {
        this._productService.productsList = results;
      });
    }
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadData() {
    this._invoiceService.toGenerate.pipe(takeUntil(this._unsubscribeAll)).subscribe((invoices: IInvoice[]) => {
      this.invoices = invoices;
      if (invoices.length > 0) {
        this.filterInvoices();
        this._changeDetectorRef.detectChanges();
      }
      this._utilsService.hideLoading();
    });
  }

  filterInvoices() {
    let filtered: any = this.invoices.map((i: any) => i);

    if (this.filterCustomer > 0) {
      filtered = filtered.filter((i: any) => (i.commission.client.id as number) === this.filterCustomer);
    }

    if (this.filterYear > 0) {
      filtered = filtered.filter((i: any) => i.year === this.filterYear);
    }

    if (this.filterSearch.length > 0) {
      const key: string = this.filterSearch.toLowerCase();
      filtered = filtered.filter((i: any) => i.commission_name.toLowerCase().includes(key));
    }

    this.filterMonth = Number(this.filterMonth);

    if (this.filterMonth > 0) {
      filtered = filtered.filter((i: any) => i.month === this.filterMonth);
    }

    if (this.filter_detachment === 1) {
      filtered = filtered.filter((item: any) => item.detachment_type == "delta");
    } else if (this.filter_detachment === 2) {
      filtered = filtered.filter((item: any) => item.detachment_type == "distacco");
    }

    if (this.filter_type_order > -1) {
      if (this.filter_type_order === 1) {
        filtered = filtered.filter((item: any) => item.commission_type === "task");
      } else if (this.filter_type_order === 0) {
        filtered = filtered.filter((item: any) => item.commission_type === "time_material");
      } else if (this.filter_type_order === 2) {
        filtered = filtered.filter((item: any) => item.commission_type === "product");
      }
    }

    filtered = filtered.map((item: any) => ({ ...item, month_name: moment(`${item.year}-${item.month}-1`, "YYYY-M-D").format("MMMM"), date: `${item.year}-${item.month}-01` }));

    this.filteredInvoices = filtered;

    this.sumTotalsAmount = filtered.map((item: any) => item.estimate_profit).reduce((sum: number, total: number) => Number(sum) + Number(total), 0);

    this._changeDetectorRef.detectChanges();
  }

  toggleInvoice(checked: boolean, invoice: IInvoice) {
    if (checked) {
      this.idsToGenerate.push(invoice.analytics_id as number);
    } else {
      this.idsToGenerate = this.idsToGenerate.filter((i: number) => i !== invoice.analytics_id);
    }
  }

  saveQueryParams() {
    this.router.navigate([], {
      queryParams: {
        filterSearch: this.filterSearch,
        filterCustomer: this.filterCustomer,
        filterYear: this.filterYear,
        filterMonth: this.filterMonth
      },
      queryParamsHandling: "merge"
    });
  }

  async archiveInvoice(invoice: any) {
    if (!this._authenticationService.hasCapability("archive_invoice")) {
      window.history.back();
      return false;
    }

    await this._utilsService.translate("ALERT.CONFIRM_ARCHIVE_INVOICE")
      .then(async (text: string) => {
        await this._utilsService.showSWALDeleteConfirmation({ text: text.replace("COMMISSION_NAME", invoice.commission_name), confirmButtonText: "ARCHIVE", customClass: { confirmButton: "btn btn-warning" } })
          .then((res: any) => {
            if (res.isConfirmed) {
              let analytics_ids: number[] = (invoice.users || []).map((user: any) => Number(user.analytics_id));
              if (analytics_ids.length < 1) {
                analytics_ids = [invoice.analytics_id];
              }
              this._invoiceService.archive({ analytics_ids, commission_type: invoice.commission_type }).subscribe();
            } else {
              this._utilsService.showSWALError({ title: "ABORTED_TITLE", text: "ABORTED_TEXT" });
            }
          });
      });
  }
}
